import { withTranslation } from 'react-i18next';

import { Box } from '@mui/material';

import { MessageText, MessageTimestamp } from 'stream-chat-react';

import ChatQuotedMessage from '../ChatQuotedMessage';
import ChatAttachments from '../ChatAttachments';
import MessageContextMenu from './MessageContextMenu';

import { IChatMessageView } from './interfaces';

import { useStyles } from './styles';
import { useMytaverseEvent } from '../../../providers';
import { Message } from 'stream-chat/dist/types/types';
import { DefaultStreamChatGenerics } from 'stream-chat-react/dist/types/types';
import { useChatState } from '../../../../../hooks/context';
import {
  useNotification,
  getErrorNotification,
  NOTIFICATION_TYPES,
} from '../../../../../components/Notification';
import { getNotification } from '../../../../../components/Notification/helpers';
import { getCatchErrorMessage } from '../../../../../helpers/error';

const ChatMessageView = ({
  message,
  isMyMessage,
  firstMessageOfGroup = false,
  channel,
  showFailedMessage,
  t: translate,
}: IChatMessageView) => {
  const { participants } = useMytaverseEvent();
  const { resolvedFailedMessageIds, setResolvedFailedMessageIds } =
    useChatState();
  const { showNotification } = useNotification();

  const classes = useStyles({
    isMyMessage,
    firstMessageOfGroup,
  });

  const participant = participants.find(
    (participant) => participant.streamChatMemberId === message.user?.id,
  );

  const messageAuthor = isMyMessage
    ? translate('chatDrawer.you')
    : participant?.name || '';

  const handleResendMessage = async () => {
    if (!!channel && message.status === 'failed') {
      try {
        const {
          created_at,
          updated_at,
          id,
          status,
          latest_reactions,
          own_reactions,
          reaction_counts,
          reply_count,
          ...messageData
        } = message;
        await channel.sendMessage(
          messageData as Message<DefaultStreamChatGenerics>,
        );
        // successfully resending a message
        const newResolvedFailedMessageIds = [...resolvedFailedMessageIds];
        newResolvedFailedMessageIds.push(id);
        setResolvedFailedMessageIds(newResolvedFailedMessageIds);
        showNotification(
          getNotification({
            message: translate('notifications.chat.resendingMessageSuccess'),
            type: NOTIFICATION_TYPES.SUCCESS,
          }),
        );
      } catch (error) {
        showNotification(
          getErrorNotification({
            message: translate('notifications.chat.resendingMessageFailed'),
          }),
        );
        throw Error(getCatchErrorMessage(error));
      }
    }
  };

  if (showFailedMessage) {
    return (
      <Box sx={classes.root}>
        <Box sx={classes.infoContainer}>
          {firstMessageOfGroup && (
            <Box sx={classes.userContainer}>
              <Box sx={classes.userName}>{messageAuthor}</Box>
              <Box sx={classes.date}>
                <MessageTimestamp format="MMM D, h:mm A" />
              </Box>
            </Box>
          )}
          <Box sx={classes.messageContainer}>
            <Box>
              <Box
                onClick={handleResendMessage}
                sx={{ ...classes.messageFailed, ...classes.message }}
              >
                {message.quoted_message ? (
                  <ChatQuotedMessage message={message} />
                ) : (
                  <MessageText />
                )}
              </Box>
              {!!message.attachments && message.attachments.length > 0 && (
                <ChatAttachments attachments={message.attachments} />
              )}
            </Box>
            <MessageContextMenu />
          </Box>
        </Box>
      </Box>
    );
  } else if (message.status !== 'failed') {
    return (
      <Box sx={classes.root}>
        <Box sx={classes.infoContainer}>
          {firstMessageOfGroup && (
            <Box sx={classes.userContainer}>
              <Box sx={classes.userName}>{messageAuthor}</Box>
              <Box sx={classes.date}>
                <MessageTimestamp format="MMM D, h:mm A" />
              </Box>
            </Box>
          )}
          <Box sx={classes.messageContainer}>
            <Box>
              <Box sx={classes.message}>
                {message.quoted_message ? (
                  <ChatQuotedMessage message={message} />
                ) : (
                  <MessageText />
                )}
              </Box>
              {!!message.attachments && message.attachments.length > 0 && (
                <ChatAttachments attachments={message.attachments} />
              )}
            </Box>
            <MessageContextMenu />
          </Box>
        </Box>
      </Box>
    );
  } else {
    return null;
  }
};

export default withTranslation('common')(ChatMessageView);
